/**
 * useSearchFilterParams
 *
 * The single source of truth for search & filter state in the public app.
 * All state lives in the URL — no local React state for committed values.
 *
 * Contract:
 *   - `searchKey` param  → search mode   (clears all filter keys on set)
 *   - filter keys        → filter mode   (clears `searchKey` on set)
 *   - neither            → default mode  (fetches all data)
 *   - `pageKey`          → current page  (preserved across mode changes when navigating;
 *                                         reset to 1 whenever mode or params change)
 *
 * FilterValue typing:
 *   The hook uses each FilterSection's `type` field to deserialize URL strings
 *   into properly-typed JavaScript values:
 *
 *   | type        | URL             | FilterValue           |
 *   |-------------|-----------------|-----------------------|
 *   | select      | ?k=value        | string | null         |
 *   | multiSelect | ?k=a&k=b        | string[]              |
 *   | boolean     | ?k=true/false   | boolean | null        |
 *   | number      | ?k=42           | number | null         |
 *   | date        | ?k=2025-06-01   | string | null         |
 *   | text        | ?k=term         | string | null         |
 */

import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";

import type { FilterSection, FilterState, FilterValue } from "@/types/filterTypes";

// ─── Types ──────────────

export type SearchFilterMode = "search" | "filter" | "default";

export interface SearchFilterSnapshot {
  mode: SearchFilterMode;
  searchTerm: string;
  filterState: FilterState;
  page: number;
  size: number;
  apiFilters: FilterState;
}

export interface SearchFilterActions {
  setSearch: (term: string) => void;
  setFilters: (state: FilterState) => void;
  removeFilter: (key: string, urlValue: string) => void;
  clearFilters: () => void;
  setPage: (page: number) => void;
  setPageSize: (size: number) => void;
  clearSearchAndFilters: () => void;
  getActiveFilterCount: () => number;
}

export type UseSearchFilterParamsReturn = SearchFilterSnapshot & SearchFilterActions;

export interface SearchFilterOptions {
  searchKey?: string;
  pageKey?: string;
  sizeKey?: string;
  defaultPage?: number;
  defaultSize?: number;
}

// ─── Serialization helpers ──────────────────────────────────────────────────

const isEmptyFilterValue = (value: FilterValue) => {
  if (value === null || value === undefined) {
    return true;
  }

  if (typeof value === "string") {
    return value.trim() === "";
  }

  if (Array.isArray(value)) {
    return value.length === 0;
  }

  return false;
};

function deserializeFilterState(params: URLSearchParams, sections: FilterSection[]): FilterState {
  const state: FilterState = {};

  sections.forEach((section) => {
    const type = section.type ?? "select";

    switch (type) {
      case "multiSelect":
        state[section.key] = params.getAll(section.key);
        break;

      case "boolean": {
        const raw = params.get(section.key);
        state[section.key] = raw === null ? null : raw === "true";
        break;
      }

      case "number": {
        const raw = params.get(section.key);
        if (raw === null) {
          state[section.key] = null;
          break;
        }

        const parsed = Number(raw);
        state[section.key] = Number.isFinite(parsed) ? parsed : null;
        break;
      }

      default:
        state[section.key] = params.get(section.key);
    }
  });

  return state;
}

function serializeFilterState(state: FilterState, sections: FilterSection[]): URLSearchParams {
  const next = new URLSearchParams();

  sections.forEach((section) => {
    const value: FilterValue = state[section.key];

    if (isEmptyFilterValue(value)) {
      return;
    }

    const type = section.type ?? "select";

    if (type === "multiSelect" && Array.isArray(value)) {
      value.forEach((v) => {
        if (v !== "") {
          next.append(section.key, v);
        }
      });

      return;
    }

    if (!Array.isArray(value)) {
      const str = String(value);

      if (str !== "") {
        next.set(section.key, str);
      }
    }
  });

  return next;
}

function hasActiveFilters(state: FilterState): boolean {
  return Object.values(state).some((value) => {
    if (isEmptyFilterValue(value)) {
      return false;
    }

    if (Array.isArray(value)) {
      return value.length > 0;
    }

    if (typeof value === "string") {
      return value.trim().length > 0;
    }

    return true;
  });
}

// ─── Hook ───────────────

export function useSearchFilterParams(
  sections: FilterSection[] = [],
  options: SearchFilterOptions = {}
): UseSearchFilterParamsReturn {
  // Derive configuration with fallbacks to remove magic values
  const searchKey = options.searchKey ?? "q";
  const pageKey = options.pageKey ?? "page";
  const sizeKey = options.sizeKey ?? "size";
  const defaultPage = options.defaultPage ?? 1;
  const defaultSize = options.defaultSize ?? 10;

  const [searchParams, setSearchParams] = useSearchParams();

  // ─── Derived URL State

  const searchTerm = searchParams.get(searchKey) ?? "";

  const pageParam = Number(searchParams.get(pageKey));
  const page = Number.isFinite(pageParam) && pageParam > 0 ? pageParam : defaultPage;

  const sizeParam = Number(searchParams.get(sizeKey));
  const size = Number.isFinite(sizeParam) && sizeParam > 0 ? sizeParam : defaultSize;

  const filterState = useMemo(
    () => deserializeFilterState(searchParams, sections),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [searchParams.toString(), sections]
  );

  const mode: SearchFilterMode = searchTerm ? "search" : hasActiveFilters(filterState) ? "filter" : "default";

  // Actions
  const setSearch = useCallback(
    (term: string) => {
      const trimmed = term.trim();

      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);

          // Clear all filter params
          sections.forEach((section) => {
            next.delete(section.key);
          });

          // Reset pagination
          next.delete(pageKey);

          if (trimmed) {
            next.set(searchKey, trimmed);
          } else {
            next.delete(searchKey);
          }

          return next;
        },
        { replace: true }
      );
    },
    [sections, setSearchParams, searchKey, pageKey]
  );

  const setFilters = useCallback(
    (state: FilterState) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);

          // Search mode and filter mode are mutually exclusive
          next.delete(searchKey);

          // Reset pagination
          next.delete(pageKey);

          // Remove existing filter keys
          sections.forEach((section) => {
            next.delete(section.key);
          });

          // Add new filter values
          const serialized = serializeFilterState(state, sections);

          serialized.forEach((value, key) => {
            next.append(key, value);
          });

          return next;
        },
        { replace: true }
      );
    },
    [sections, setSearchParams, searchKey, pageKey]
  );

  const removeFilter = useCallback(
    (key: string, urlValue: string) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);

          const remaining = next.getAll(key).filter((v) => v !== urlValue);

          next.delete(key);

          remaining.forEach((v) => {
            next.append(key, v);
          });

          next.delete(pageKey);

          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams, pageKey]
  );

  const clearFilters = useCallback(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);

        // Strictly clear only the keys defined in the sections parameter
        sections.forEach((section) => {
          next.delete(section.key);
        });

        return next;
      },
      { replace: true }
    );
  }, [sections, setSearchParams]);

  const setPage = useCallback(
    (p: number) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);

          if (p <= 1) {
            next.delete(pageKey);
          } else {
            next.set(pageKey, String(p));
          }

          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams, pageKey]
  );

  const setPageSize = useCallback(
    (s: number) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);

          if (s <= 1) {
            next.delete(sizeKey);
          } else {
            next.set(sizeKey, String(s));
          }

          next.delete(pageKey);

          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams, sizeKey, pageKey]
  );

  const clearSearchAndFilters = useCallback(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);

        // Clear search and pagination
        next.delete(searchKey);
        next.delete(pageKey);

        // Strictly clear only the keys defined in the sections parameter
        sections.forEach((section) => {
          next.delete(section.key);
        });

        return next;
      },
      { replace: true }
    );
  }, [sections, searchKey, pageKey, setSearchParams]);

  // ─── API Filters ──────

  const apiFilters = useMemo(() => {
    const apiFilter: FilterState = {};

    Object.entries(filterState).forEach(([key, value]) => {
      if (isEmptyFilterValue(value)) {
        return;
      }

      if (Array.isArray(value)) {
        if (value.length > 0) {
          apiFilter[key] = value;
        }
        return;
      }

      if (typeof value === "string") {
        if (value.trim() !== "") {
          apiFilter[key] = value;
        }
        return;
      }

      apiFilter[key] = value;
    });

    return apiFilter;
  }, [filterState]);

  const getActiveFilterCount = useCallback(() => {
    return sections.reduce((sum, section) => {
      const value = filterState[section.key];

      if (isEmptyFilterValue(value)) {
        return sum;
      }

      if (Array.isArray(value)) {
        return sum + value.length;
      }

      if (typeof value === "string") {
        return sum + (value.trim() ? 1 : 0);
      }

      return sum + 1;
    }, 0);
  }, [sections, filterState]);

  return {
    mode,
    searchTerm,
    filterState,
    page,
    size,
    apiFilters,
    setSearch,
    setFilters,
    removeFilter,
    clearFilters,
    setPage,
    setPageSize,
    clearSearchAndFilters,
    getActiveFilterCount
  };
}